import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const SubscriptionsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    fetchHistory();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/financial-analytics`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/financial-history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch financial history:', err);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const MetricCard = ({ title, value, icon, bgColor, textColor, subtitle }) => (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 border border-gray-200">
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className={`p-3 rounded-lg ${bgColor}`}>
            {icon}
          </div>
          <div className={`text-3xl font-bold ${textColor}`}>
            {value}
          </div>
        </div>
        <h3 className="text-gray-700 font-semibold text-base">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading subscription analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Subscriptions</h1>
          <p className="text-purple-200 text-sm">Subscription metrics, growth trends, and lifecycle analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => {
              fetchAnalytics();
              fetchHistory();
            }}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Subscriptions</h1>
              <p className="text-purple-200 text-sm">Subscription metrics, growth trends, and lifecycle analytics</p>
            </div>
          </div>
          <button
            onClick={() => {
              fetchAnalytics();
              fetchHistory();
            }}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-sm text-gray-600">
        Last updated: {formatDateTime(analytics.last_updated)}
      </div>

      {/* Subscription Metrics Cards */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Subscription Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Active Subscriptions"
            value={analytics.active_subscriptions}
            subtitle="Total active subscriptions"
            icon={<svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            bgColor="bg-purple-50"
            textColor="text-purple-800"
          />
          <MetricCard
            title="Trial Subscriptions"
            value={analytics.trial_subscriptions}
            subtitle="Currently in trial period"
            icon={<svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            bgColor="bg-blue-50"
            textColor="text-blue-800"
          />
          <MetricCard
            title="Cancelled This Month"
            value={analytics.cancelled_subscriptions}
            subtitle="Cancellations"
            icon={<svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            bgColor="bg-red-50"
            textColor="text-red-800"
          />
          <MetricCard
            title="Trial Conversion Rate"
            value={`${analytics.trial_conversion_rate}%`}
            subtitle="Trials → Paid"
            icon={<svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
            bgColor="bg-indigo-50"
            textColor="text-indigo-800"
          />
        </div>
      </div>

      {/* Active Subscriptions by Tier Table */}
      {Object.keys(analytics.active_subscriptions_by_tier || {}).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Subscriptions by Tier</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(analytics.active_subscriptions_by_tier).map(([tier, count]) => (
              <div key={tier} className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <span className="text-gray-700 font-medium">{tier}</span>
                <span className="text-indigo-800 font-bold text-2xl">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscription Charts */}
      {history && history.months && (
        <div className="space-y-8">
          {/* Chart 5: Active Subscriptions by Tier (Bar Chart) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Subscriptions by Tier (Visual)</h3>
            {analytics && Object.keys(analytics.active_subscriptions_by_tier || {}).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={Object.entries(analytics.active_subscriptions_by_tier).map(([tier, count]) => ({
                  tier,
                  count
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="tier" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#8b5cf6" name="Active Subscriptions" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No subscription data available. Create PayPal subscriptions to see data.
              </div>
            )}
            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> The current distribution of active paying customers across your subscription tiers.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> If most customers are on Starter, consider improving upgrade paths with feature comparisons or limited-time upgrade offers. A healthy SaaS typically has 60% mid-tier, 30% low-tier, 10% high-tier. Adjust your pricing and features to guide customers toward higher tiers.
              </p>
            </div>
          </div>

          {/* Chart 6: Subscription Growth Trend */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Growth Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={history.months}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="new_subscriptions" stroke="#10b981" strokeWidth={2} name="New Subscriptions" />
                <Line type="monotone" dataKey="cancellations" stroke="#ef4444" strokeWidth={2} name="Cancellations" />
                <Line type="monotone" dataKey="net_growth" stroke="#3b82f6" strokeWidth={2} name="Net Growth" />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> Monthly new subscriptions, cancellations, and the net growth (new minus cancelled) over the last 12 months.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> The blue line (Net Growth) is your key metric. Positive net growth means you're gaining more customers than you're losing. If cancellations are rising, investigate why customers are leaving and address pain points. Consistent new subscriptions indicate effective marketing.
              </p>
            </div>
          </div>

          {/* Chart 7: Trial vs Paid Subscriptions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Trial vs Paid Subscriptions Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={history.months}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="trials" stackId="a" fill="#fbbf24" name="Trial" />
                <Bar dataKey="paid" stackId="a" fill="#8b5cf6" name="Paid" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> The mix of trial (yellow) and paid (purple) subscriptions each month over the last 12 months.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> A growing purple (paid) section shows successful trial conversions and customer retention. If yellow (trials) is consistently large but purple doesn't grow proportionally, your trial-to-paid conversion needs improvement. Consider shortening trials, improving onboarding, or adding trial engagement tactics.
              </p>
            </div>
          </div>

          {/* Chart 8: Subscription Lifecycle Funnel */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Lifecycle Funnel</h3>
            {analytics && analytics.funnel_data ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={analytics.funnel_data}
                  layout="horizontal"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="stage" type="category" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#f97316" name="Customers" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                Loading funnel data...
              </div>
            )}
            <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> A funnel visualization showing customer drop-off at each stage: Trial Started → Trial Active → Converted → Active → Churned.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Identify where customers are dropping off. Large drop from Trial Started to Trial Active? Improve onboarding emails. Big drop at Converted? Trial experience isn't convincing - enhance key features. High churn after Active? Address product-market fit or customer support issues. Each stage reveals specific optimization opportunities.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsPage;
